import { Search, Filter, Sort, ButtonDropdown, Filters, SelectFilterConfig, Button } from '@cimplico/components';
import { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { FilterResults, SortResult } from '../../components/primary-page-actions/PrimaryPageActions';
import { useFirmContext } from '../../providers/FirmProvider';
import { useEntity } from '../../queries/use-entity';
import { useRefreshFYIDocuments } from '../../queries/use-refresh-fyi-documents';
import { useUsers } from '../../queries/use-users';
import { useWorkpaper } from '../../queries/use-workpaper';
import { saveFilter, saveSort } from '../../slices/workpaperFilterSlice';
import { RootState } from '../../store';
import { selector } from '../../slices/filesSlice';
import ReSyncFYIButton from './ReSyncFYIButton';

type FilesPagePrimaryActionsProps = {
    workpaperId?: string;
    entityId?: string;
    useSmallStyling?: boolean;
    onSearch: (search: string) => void;
    onSort: (sort: string) => void;
    onFilter: (filter: string) => void;
    onNewFileClick: () => void;
    onNewLinkClick: () => void;
    onViewLocationClick: () => void;
    disableLocationButton?: boolean;
    disablePrimaryButton?: boolean;
    searchRef?: React.MutableRefObject<HTMLInputElement | undefined>;
    defaultSearchValue?: string;
};

const sort = [
    { id: 'name', label: 'Name' },
    { id: 'items', label: 'Item Count' },
    { id: 'recordType', label: 'Record Type' },
    { id: 'updatedAt', label: 'Last Updated' },
];

function FilesPagePrimaryActions({
    workpaperId,
    entityId,
    useSmallStyling = false,
    onFilter,
    onNewFileClick,
    onNewLinkClick,
    onViewLocationClick,
    disableLocationButton,
    onSearch,
    onSort,
    disablePrimaryButton = false,
    searchRef,
    defaultSearchValue = '',
}: FilesPagePrimaryActionsProps): JSX.Element {
    const firm = useFirmContext();
    const usersQuery = useUsers({ page: 1, search: '', sort: '', filter: '' });
    const [params, setParams] = useSearchParams();

    const savedFilters = useSelector((state: RootState) => state.workpaperFilters.value[workpaperId || '']?.files);
    const dispatch = useDispatch();

    function handleSortChange(result: SortResult): void {
        workpaperId && dispatch(saveSort({ workpaperId, section: 'files', sort: result.currentSortState, qs: result.queryString }));
        onSort(result.queryString);
    }

    function handleFilterChange(result: FilterResults): void {
        workpaperId && dispatch(saveFilter({ workpaperId, section: 'files', filters: result.currentFilterState, qs: result.queryString }));
        onFilter(result.queryString);
    }

    function handleSearchChange(result: string): void {
        onSearch(result);
    }

    useEffect(() => {
        setParams((prev) => {
            prev.delete('status');
            return prev;
        });
    }, [setParams]);

    const createdBy = savedFilters?.filter?.find((f) => f.id === 'createdBy' && f.type === 'select') as SelectFilterConfig | undefined;

    const filterOptions: Filters = useMemo(() => {
        return [
            params.get('status') === 'CHECKED_OUT'
                ? {
                      title: 'Status',
                      id: 'status',
                      type: 'checkbox',
                      options: [
                          { label: 'Checked In', value: 'CHECKED_IN', active: false },
                          { label: 'Checked Out', value: 'CHECKED_OUT', active: true },
                          { label: 'Not Applicable', value: 'NOT_APPLICABLE', active: false },
                      ],
                  }
                : savedFilters?.filter?.find((f) => f.id === 'status') ?? {
                      title: 'Status',
                      id: 'status',
                      type: 'checkbox',
                      options: [
                          { label: 'Checked In', value: 'CHECKED_IN', active: false },
                          { label: 'Checked Out', value: 'CHECKED_OUT', active: false },
                          { label: 'Not Applicable', value: 'NOT_APPLICABLE', active: false },
                      ],
                  },
            params.get('status') === 'CHECKED_OUT'
                ? {
                      title: 'Record Type',
                      id: 'recordType',
                      type: 'checkbox',
                      options: [
                          { label: 'Document', value: 'DOCUMENT', active: false },
                          { label: 'Link', value: 'LINK', active: false },
                          { label: 'Reference', value: 'REFERENCE', active: false },
                          { label: 'Folder', value: 'FOLDER', active: false },
                          ...(firm.integrations.fyi ? [{ label: 'FYI Document', value: 'FYI_DOCUMENT', active: false }] : []),
                      ],
                  }
                : savedFilters?.filter?.find((f) => f.id === 'recordType') ?? {
                      title: 'Record Type',
                      id: 'recordType',
                      type: 'checkbox',
                      options: [
                          { label: 'Document', value: 'DOCUMENT', active: false },
                          { label: 'Link', value: 'LINK', active: false },
                          { label: 'Reference', value: 'REFERENCE', active: false },
                          { label: 'Folder', value: 'FOLDER', active: false },
                          ...(firm.integrations.fyi ? [{ label: 'FYI Document', value: 'FYI_DOCUMENT', active: false }] : []),
                      ],
                  },
            params.get('status') === 'CHECKED_OUT'
                ? {
                      title: 'Item Count',
                      id: 'items',
                      type: 'numeric',
                      option: { condition: '=' },
                      isPercent: false,
                  }
                : savedFilters?.filter?.find((f) => f.id === 'items') ?? {
                      title: 'Item Count',
                      id: 'items',
                      type: 'numeric',
                      option: { condition: '=' },
                      isPercent: false,
                  },
            params.get('status') === 'CHECKED_OUT'
                ? {
                      title: 'Last Updated',
                      id: 'updatedAt',
                      type: 'date',
                      option: { condition: '=' },
                  }
                : savedFilters?.filter?.find((f) => f.id === 'updatedAt') ?? {
                      title: 'Last Updated',
                      id: 'updatedAt',
                      type: 'date',
                      option: { condition: '=' },
                  },
            params.get('status') === 'CHECKED_OUT'
                ? {
                      title: 'Created By',
                      id: 'createdBy',
                      isMulti: true,
                      options: (usersQuery.data?.users ?? []).map((user) => ({
                          active: createdBy?.options.find((o) => o.value === user.id && o.active) !== undefined,
                          label: `${user.firstName} ${user.lastName}`,
                          value: user.id,
                      })),
                      type: 'select',
                  }
                : savedFilters?.filter?.find((f) => f.id === 'createdBy') ?? {
                      title: 'Created By',
                      id: 'createdBy',
                      isMulti: true,
                      options: (usersQuery.data?.users ?? []).map((user) => ({
                          active: createdBy?.options.find((o) => o.value === user.id && o.active) !== undefined,
                          label: `${user.firstName} ${user.lastName}`,
                          value: user.id,
                          //   decoration: <UserAvatar size="xs" user={user} />,
                      })),
                      type: 'select',
                  },
        ];
    }, [createdBy?.options, firm.integrations.fyi, params, savedFilters?.filter, usersQuery.data?.users]);

    return (
        <div className="flex gap-4 items-center">
            <div className="w-[280px]">
                <Search
                    onChange={handleSearchChange}
                    placeholder="Search here and sub-folders..."
                    inputRef={searchRef}
                    defaultValue={defaultSearchValue}
                />
            </div>
            <Sort
                size={useSmallStyling ? 'small' : 'regular'}
                onChange={handleSortChange}
                sortOptions={sort}
                enableV2Query
                defaultSortOption={savedFilters?.sort?.option ?? 'name'}
                defaultSortOrder={savedFilters?.sort?.order}
            />
            <Filter
                onChange={handleFilterChange}
                filters={filterOptions}
                enableV2Query
                size={useSmallStyling ? 'small' : 'regular'}
                menuPosition="left"
            />
            {/* <ReSyncFYIButton
                workpaperId={workpaperId}
                entityId={entityId}
                useSmallStyling={useSmallStyling}
                disabled={disablePrimaryButton}
            /> */}
            <Button
                label="View Location"
                size={useSmallStyling ? 'xs' : 'sm'}
                type="secondary"
                onClick={onViewLocationClick}
                disabled={disableLocationButton}
            />
            <ButtonDropdown
                variant="primary"
                label="New File"
                onClick={onNewFileClick}
                disabled={disablePrimaryButton}
                size={useSmallStyling ? 'xs' : 'sm'}
            >
                <ButtonDropdown.SecondaryAction label="New Link" onClick={onNewLinkClick} />
            </ButtonDropdown>
        </div>
    );
}
export default FilesPagePrimaryActions;
